import { useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildAuthHeaders } from "../utils/apiAuth";
import { useAuthToken } from "../hooks/useAuthToken";
import {
  BrainConfig,
  BrainRun,
  Domain,
  DomainInput,
  NoopResult,
  Requirement,
  TranscriptSummary,
  WikiDoc,
} from "./types";
import { getApiUrl } from "../utils/runtimeConfig";

/** True when a triggerRun result is a no-op (nothing to do; no job created). */
export const isNoop = (r: BrainRun | NoopResult): r is NoopResult =>
  (r as NoopResult)?.noop === true;

const API_URL = getApiUrl();

async function authGet<T>(
  path: string,
  getToken: () => Promise<string>,
  user?: { email?: string }
): Promise<T> {
  const headers = await buildAuthHeaders(getToken, user);
  const res = await fetch(`${API_URL}${path}`, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function authSend<T>(
  method: string,
  path: string,
  getToken: () => Promise<string>,
  user: { email?: string } | undefined,
  body?: unknown,
  requireToken = false
): Promise<T> {
  const headers = await buildAuthHeaders(getToken, user, { requireToken });
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { ...headers, "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function authGetText(
  path: string,
  getToken: () => Promise<string>,
  user?: { email?: string }
): Promise<string> {
  const headers = await buildAuthHeaders(getToken, user);
  const res = await fetch(`${API_URL}${path}`, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || `HTTP ${res.status}`);
  }
  return res.text();
}

async function authUpload<T>(
  path: string,
  getToken: () => Promise<string>,
  user: { email?: string } | undefined,
  file: File
): Promise<T> {
  // No Content-Type header — the browser sets the multipart boundary.
  const headers = await buildAuthHeaders(getToken, user);
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_URL}${path}`, { method: "POST", headers, body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || res.statusText || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function useBrainApi() {
  const { accounts, instance } = useMsal();
  const getToken = useAuthToken();

  // account/user are computed inside useMemo — not in the dep array — so a new
  // object reference for accounts[0] on each render doesn't invalidate the memo
  // and trigger an infinite re-fetch loop in every page that depends on `api`.
  return useMemo(
    () => {
      const account = accounts[0] || instance.getActiveAccount();
      const user = account ? { email: account.username } : undefined;
      return {
      getRuns: (take = 200) =>
        authGet<BrainRun[]>(`/api/brain/runs?take=${take}`, getToken, user),
      getRequirements: (status?: string) => {
        const q = status ? `?status=${encodeURIComponent(status)}` : "";
        return authGet<Requirement[]>(`/api/brain/requirements${q}`, getToken, user);
      },
      getConfig: () => authGet<BrainConfig>("/api/brain/config", getToken, user),
      // requireToken: true → human-decision writes attach the Entra JWT (server-enforced DagUser policy).
      updateConfig: (cfg: { docsModel?: string | null; requirementsModel?: string | null }) =>
        authSend<BrainConfig>("PUT", "/api/brain/config", getToken, user, cfg, true),
      listDomains: () => authGet<Domain[]>("/api/domains", getToken, user),
      createDomain: (d: DomainInput) =>
        authSend<Domain>("POST", "/api/domains", getToken, user, d, true),
      updateDomain: (id: number, d: DomainInput) =>
        authSend<Domain>("PUT", `/api/domains/${id}`, getToken, user, d, true),
      deleteDomain: (id: number) =>
        authSend<void>("DELETE", `/api/domains/${id}`, getToken, user, undefined, true),
      listTranscripts: (id: number) =>
        authGet<TranscriptSummary[]>(`/api/domains/${id}/transcripts`, getToken, user),
      uploadTranscript: (id: number, file: File) =>
        authUpload<TranscriptSummary>(`/api/domains/${id}/transcripts`, getToken, user, file),
      sendRequirement: (id: number) =>
        authSend<Requirement>("POST", `/api/brain/requirements/${id}/send`, getToken, user, undefined, true),
      rejectRequirement: (id: number) =>
        authSend<Requirement>("POST", `/api/brain/requirements/${id}/reject`, getToken, user, undefined, true),
      // workflow omitted → full domain ingestion (sync + source pair); set to "sync"/"source" to
      // run just one stage (retry independently).
      triggerRun: (tenant: string, workflow?: string) =>
        authSend<BrainRun | NoopResult>("POST", "/api/brain/runs", getToken, user, {
          tenant,
          workflow,
        }),
      cancelRun: (id: number) =>
        authSend<BrainRun>("POST", `/api/brain/runs/${id}/cancel`, getToken, user),
      listWikiDocs: (domain: string) =>
        authGet<WikiDoc[]>(`/api/brain/docs/${encodeURIComponent(domain)}`, getToken, user),
      readWikiDoc: (domain: string, path: string) =>
        authGetText(
          `/api/brain/docs/${encodeURIComponent(domain)}/content?path=${encodeURIComponent(path)}`,
          getToken,
          user
        ),
    };
    },
    [getToken, accounts, instance]
  );
}

export interface LiveData<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

export function useLiveData<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  intervalMs = 10000
): LiveData<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const tickRef = useRef<() => void>(() => {});

  useEffect(() => {
    let active = true;
    // inFlight is LOCAL to this effect instance so a re-subscribe (dep change or
    // remount) always fetches immediately rather than waiting a full interval.
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, intervalMs);
    };
    const run = async () => {
      if (!active) return;
      if (typeof document !== "undefined" && document.hidden) {
        schedule();
        return;
      }
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await fetcherRef.current();
        if (active) {
          setData(d);
          setError(null);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "request failed");
      } finally {
        inFlight = false;
        if (active) {
          setLoading(false);
          schedule();
        }
      }
    };
    tickRef.current = () => {
      clearTimeout(timer);
      void run();
    };
    const onVisible = () => {
      if (!document.hidden) tickRef.current();
    };

    void run();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs]);

  return { data, error, loading, refresh: () => tickRef.current() };
}